import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoXL:'#eef2ff',
  snow:    '#f8fafc',
  white:   '#ffffff',
};

const Section = ({ id, title, children }) => (
  <section id={id} style={{ paddingBottom: 44, borderBottom: `1px solid ${C.border}`, marginBottom: 44 }}>
    <h2 style={{ fontSize: 'clamp(1.1rem,2vw,1.35rem)', fontWeight: 800, color: C.navy, margin: '0 0 16px', letterSpacing: '-0.02em' }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.85, margin: '0 0 14px' }}>{children}</p>
);

const Ul = ({ items }) => (
  <ul style={{ margin: '0 0 14px', paddingLeft: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0', fontSize: '0.9rem', color: C.muted, lineHeight: 1.7 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.indigo, marginTop: 9, flexShrink: 0 }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const StatusCard = ({ icon, label, status, statusColor, statusBg, body }) => (
  <div style={{ background: C.snow, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 20px', marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.2rem' }}>{icon}</span>
        <span style={{ fontWeight: 800, fontSize: '0.9rem', color: C.navy }}>{label}</span>
      </div>
      <span style={{ fontSize: '0.68rem', fontWeight: 800, padding: '3px 10px', borderRadius: 99, background: statusBg, color: statusColor, border: `1px solid ${statusColor}30` }}>{status}</span>
    </div>
    <div style={{ fontSize: '0.85rem', color: C.muted, lineHeight: 1.7, paddingLeft: 34 }}>{body}</div>
  </div>
);

const LegalFooter = () => (
  <footer style={{ background: C.snow, padding: '24px clamp(20px,5vw,60px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img src="/logo192.png" alt="AssessMonk" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'contain' }} />
      <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>AssessMonk</span>
    </div>
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <Link to="/privacy"    style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
      <Link to="/terms"      style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Terms</Link>
      <Link to="/disclaimer" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Disclaimer</Link>
      <Link to="/refund"     style={{ fontSize: '0.72rem', color: C.indigo, textDecoration: 'none', fontWeight: 700 }}>Refund Policy</Link>
      <Link to="/contact"    style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Contact</Link>
    </div>
    <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>© 2026 AssessMonk. All rights reserved.</div>
  </footer>
);

export default function RefundPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy, minHeight: '100vh' }}>

      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try AI Assessment
        </button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(to bottom, ${C.indigoXL}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) 0' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 99, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, color: C.indigo, marginBottom: 20, letterSpacing: '0.03em' }}>
            Legal · Billing
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 16px' }}>
            Refund &amp; Cancellation Policy
          </h1>
          <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.75, maxWidth: 600, margin: '0 0 24px' }}>
            AssessMonk operates a clear, no-refund subscription model. Please read this policy carefully before purchasing a plan.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: C.muted, fontWeight: 600, background: C.snow, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px', marginBottom: 48 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Last updated: 25 April 2026
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px clamp(20px,5vw,64px) 80px' }}>

        {/* No-refund callout */}
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 16, padding: '20px 24px', marginBottom: 40, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>⚠</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#7f1d1d', marginBottom: 6 }}>All subscription payments are non-refundable</div>
            <div style={{ fontSize: '0.85rem', color: '#991b1b', lineHeight: 1.65 }}>By subscribing to any paid plan on AssessMonk, you agree that all charges are final. AssessMonk does not offer refunds on subscription fees under any circumstances, except in the case of a verified duplicate charge or technical billing error on our end.</div>
          </div>
        </div>

        <Section id="plans" title="1. Available Plans & Pricing">
          <P>AssessMonk offers the following subscription plans:</P>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${C.border}`, marginBottom: 16 }}>
            {[
              { plan: 'Free',              monthly: '—', annual: '₹0',          note: '3 free lifetime assessments. No payment required.' },
              { plan: 'Student Yearly',    monthly: '—', annual: '₹1,299 / yr', note: 'Unlimited assessments, history sync, PDF export.' },
              { plan: 'Clinician Yearly',  monthly: '—', annual: '₹1,999 / yr', note: 'Priority AI output, all Student features.' },
              { plan: 'Institution',       monthly: '—', annual: '₹19,999 / yr', note: 'Unlimited student seats, admin dashboard.' },
            ].map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', borderBottom: i < 3 ? `1px solid ${C.border}` : 'none', background: i % 2 === 0 ? C.white : C.snow }}>
                <div style={{ padding: '12px 14px', fontSize: '0.82rem', fontWeight: 800, color: C.navy, borderRight: `1px solid ${C.border}` }}>{row.plan}</div>
                <div style={{ padding: '12px 14px', fontSize: '0.82rem', color: C.ink, borderRight: `1px solid ${C.border}` }}>{row.monthly}</div>
                <div style={{ padding: '12px 14px', fontSize: '0.82rem', fontWeight: 700, color: C.indigo, borderRight: `1px solid ${C.border}` }}>{row.annual}</div>
                <div style={{ padding: '12px 14px', fontSize: '0.78rem', color: C.muted }}>{row.note}</div>
              </div>
            ))}
          </div>
          <P>All paid plans are billed annually as a single upfront payment covering 12 months of access. There are no monthly billing options.</P>
        </Section>

        <Section id="subscriptions" title="2. Subscription Terms">
          <StatusCard
            icon="📅"
            label="Billing cycle"
            status="Annual only"
            statusColor="#4f46e5"
            statusBg="#eef2ff"
            body="All paid plans are billed annually — a single upfront payment covering 12 months of access. The renewal date is set at the point of first payment and recurs on the same date each year."
          />
          <StatusCard
            icon="🔄"
            label="Renewal"
            status="Manual"
            statusColor="#d97706"
            statusBg="#fffbeb"
            body="Subscriptions do not auto-renew. When your plan expires you will simply revert to the free tier. You may re-subscribe at any time to regain unlimited access."
          />
          <StatusCard
            icon="⛔"
            label="Cancellation"
            status="Any time"
            statusColor="#059669"
            statusBg="#f0fdf4"
            body="Plans are not auto-renewed. Your access simply expires at the end of the paid period and you revert to the free tier. No action is needed to avoid being charged again. No partial refunds are issued for unused time."
          />
          <StatusCard
            icon="🧾"
            label="Taxes"
            status="GST applicable"
            statusColor="#64748b"
            statusBg="#f8fafc"
            body="Prices are exclusive of applicable taxes. GST at the prevailing rate will be applied at checkout for Indian users. The final tax-inclusive amount is shown before payment confirmation."
          />
        </Section>

        <Section id="no-refund" title="3. No-Refund Policy">
          <P>AssessMonk operates a strict no-refund policy. Once a subscription payment has been processed, it is non-refundable regardless of:</P>
          <Ul items={[
            'Whether you used the platform during the subscription period.',
            'Whether you downgraded or cancelled your plan mid-cycle.',
            'Change of mind, dissatisfaction with AI outputs, or unused features.',
            'Re-subscribing for a period you did not intend to.',
            'Technical issues on your device, browser, or internet connection.',
          ]} />
          <P>By completing a subscription purchase, you explicitly acknowledge and accept this no-refund policy.</P>
        </Section>

        <Section id="exceptions" title="4. Billing Errors — The Only Exception">
          <P>The only circumstance in which a payment may be reviewed for reversal is a verified billing error originating from AssessMonk:</P>
          <Ul items={[
            'A duplicate charge applied to the same account in the same billing cycle.',
            'A charge applied after a confirmed cancellation was processed.',
            'A technical fault on our payment processor resulting in an incorrect amount being charged.',
          ]} />
          <P>If you believe you have been charged in error, contact us at <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a> within 7 days of the charge with your account email, transaction date, and amount. We will investigate and respond within 5 business days.</P>
          <P>Billing error claims submitted more than 14 days after the charge date will not be considered.</P>
        </Section>

        <Section id="institution" title="5. Institution Licenses">
          <P>Institution licenses (₹19,999/year) are billed annually as a single flat payment and cover unlimited student seats for a 12-month period.</P>
          <Ul items={[
            'Institution licenses are non-refundable once activated.',
            'Licenses are non-transferable between institutions.',
            'Renewal is at the discretion of the institution and is not automatic unless opted into.',
            'For bulk or multi-year institution pricing, contact assessmonk@gmail.com.',
          ]} />
        </Section>

        <Section id="governing-law" title="6. Governing Law">
          <P>This Refund Policy is governed by the laws of India. Any dispute relating to billing, charges, or this policy is subject to the exclusive jurisdiction of courts in Dehradun, Uttarakhand, India.</P>
          <P>AssessMonk reserves the right to update this policy at any time. Changes will be communicated via the platform and/or email. Continued use of a paid plan after changes are posted constitutes acceptance of the revised policy.</P>
        </Section>

        <div style={{ background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 14, padding: '20px 24px' }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: C.navy, marginBottom: 6 }}>Billing question or error to report?</div>
          <P>Email <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a> — include your account email and transaction details. We respond within 2 business days.</P>
        </div>

      </div>

      <LegalFooter />
    </div>
  );
}
